/**
 * Places a floating element, already in the top layer with `position: fixed`, beside its anchor.
 *
 * This is the positioning decision DS-027 asked for. CSS anchor positioning would do it with no
 * script, but Firefox and Safari ship it only in their newest releases, and a menu that opens in
 * the wrong place on a customer's phone is worse than twenty lines of measuring. The popover
 * attribute still supplies the top layer, light dismiss and Escape, so a menu is never clipped by an
 * `overflow: hidden` parent. Revisit when every browser we support has anchor positioning.
 *
 * It prefers the side asked for and flips to the other when the preferred one is too short and the
 * other is not. Horizontally it keeps the element inside the viewport.
 */

export type Side = "top" | "bottom";

export type Align = "start" | "end";

/* The least space left between a floating element and the edge of the viewport. */
const EDGE_PX = 8; // token-exempt: a measuring constant, not a drawn value

function fitsAbove(anchor: DOMRect, height: number): boolean {
  return anchor.top >= height;
}

function fitsBelow(anchor: DOMRect, height: number): boolean {
  return window.innerHeight - anchor.bottom >= height;
}

function opensAbove(side: Side, anchor: DOMRect, height: number): boolean {
  if (side === "top") return fitsAbove(anchor, height) || !fitsBelow(anchor, height);

  return !fitsBelow(anchor, height) && fitsAbove(anchor, height);
}

export function place(
  anchor: Element,
  floating: HTMLElement,
  side: Side,
  align: Align,
  gap: number,
): void {
  const from = anchor.getBoundingClientRect();
  const box = floating.getBoundingClientRect();
  const above = opensAbove(side, from, box.height + gap);
  const top = above ? from.top - box.height - gap : from.bottom + gap;
  const left = align === "end" ? from.right - box.width : from.left;
  const room = document.documentElement.clientWidth - box.width - EDGE_PX;

  floating.style.top = `${String(Math.max(EDGE_PX, top))}px`;
  floating.style.left = `${String(Math.max(EDGE_PX, Math.min(left, room)))}px`;
}

/** Calls `again` whenever the page scrolls or resizes, until the returned function is called. */
export function followUntilClosed(again: () => void): () => void {
  window.addEventListener("scroll", again, true);
  window.addEventListener("resize", again);

  return () => {
    window.removeEventListener("scroll", again, true);
    window.removeEventListener("resize", again);
  };
}
